import { ArrowLeft, ArrowRight } from "lucide-react";
import { lazy, Suspense, useEffect } from "react";
import beOneLogo from "../assets/logo.svg";
import { CompanyCapture } from "./components/CompanyCapture";
import { EmailCapture } from "./components/EmailCapture";
import { PausedScreen } from "./components/PausedScreen";
import { ProgressBar } from "./components/ProgressBar";
import { QuestionCard } from "./components/QuestionCard";
import { ThemeComplete } from "./components/ThemeComplete";
import { WelcomeScreen } from "./components/WelcomeScreen";
import { questionGroups, useAssessment } from "./hooks/useAssessment";
import { useT } from "./i18n";

// Only needed at the end of the funnel — split out of the main bundle.
const LoadingDiagnosis = lazy(() =>
  import("./components/LoadingDiagnosis").then((m) => ({
    default: m.LoadingDiagnosis,
  })),
);
const Results = lazy(() =>
  import("./components/Results").then((m) => ({ default: m.Results })),
);

export default function App() {
  const assessment = useAssessment();
  const t = useT();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [assessment.currentQuestion, assessment.phase]);

  // --- Email capture after group 1 ---
  if (assessment.phase === "emailCapture") {
    const group = questionGroups[0];
    const groupScore = assessment.calculateGroupScore(0);
    const maxScore = group.size * assessment.maxScorePerQuestion;

    return (
      <main>
        <EmailCapture
          onSaveAndContinue={assessment.saveEmailAndContinue}
          onConfirmCompanyAndContinue={assessment.confirmCompanyAndContinue}
          onPauseForLater={assessment.pauseForLater}
          onGoBack={assessment.backFromIntermediate}
          score={groupScore}
          total={maxScore}
          themeName={t(`groups.${group.num}`)}
          answers={assessment.answers}
          initialEmail={assessment.email}
          companySuggestion={assessment.companySuggestion}
          companyConfirmationRequired={assessment.companyConfirmationRequired}
        />
      </main>
    );
  }

  // --- Theme complete for groups 2-3 ---
  if (assessment.phase === "themeComplete") {
    const group = questionGroups[assessment.currentGroupIndex];

    return (
      <main>
        <ThemeComplete
          themeNumber={group.num}
          themeName={t(`groups.${group.num}`)}
          onContinue={assessment.continueAfterTheme}
          onGoBack={assessment.backFromIntermediate}
          allThemeScores={assessment.calculateAllThemeScores()}
          answers={assessment.answers}
        />
      </main>
    );
  }

  if (assessment.phase === "companyCapture") {
    return (
      <main>
        <CompanyCapture
          companySuggestion={assessment.companySuggestion}
          onConfirmCompany={assessment.confirmCompanyAndContinue}
        />
      </main>
    );
  }

  // --- Paused screen ---
  if (assessment.phase === "paused") {
    return (
      <main>
        <PausedScreen
          email={assessment.email}
          onContinueNow={assessment.continueFromPause}
        />
      </main>
    );
  }

  // --- Loading screen (AI diagnosis generation) ---
  if (assessment.phase === "loading") {
    return (
      <main>
        <Suspense fallback={null}>
          <LoadingDiagnosis />
        </Suspense>
      </main>
    );
  }

  // --- Final results ---
  if (assessment.phase === "results") {
    return (
      <main
        className="min-h-screen p-6 flex items-center justify-center"
        style={{
          background: "linear-gradient(to bottom right, #02324A, #00AFFD)",
        }}
      >
        <Suspense fallback={null}>
          <Results
            allThemeScores={assessment.calculateAllThemeScores()}
            aiDiagnosis={assessment.aiDiagnosis}
            sessionId={assessment.sessionId}
            onRetryDiagnosis={assessment.retryDiagnosis}
          />
        </Suspense>
      </main>
    );
  }

  // --- Welcome screen ---
  if (assessment.phase === "welcome") {
    return (
      <main>
        <WelcomeScreen
          totalQuestions={assessment.totalQuestions}
          onStart={assessment.start}
        />
      </main>
    );
  }

  // --- Questions screen ---
  const question = assessment.currentQuestionData;
  const currentAnswer = assessment.answers[assessment.currentQuestion];

  return (
    <main
      className="min-h-screen p-6"
      style={{
        background: "linear-gradient(to bottom right, #02324A, #00AFFD)",
      }}
    >
      <div className="max-w-4xl mx-auto p-0">
        <div className="mb-8">
          <ProgressBar
            current={assessment.currentQuestion + 1}
            total={assessment.totalQuestions}
            currentQuestionNumber={assessment.currentQuestion + 1}
            answers={assessment.answers}
          />
        </div>

        <div className="bg-white rounded-2xl shadow-xl mb-6 p-[12px]">
          <div className="mb-4 flex items-center justify-center md:justify-between">
            <img
              src={beOneLogo}
              alt="Be-one"
              width={46}
              height={20}
              className="h-5 ml-1 hidden md:block"
            />
            <span
              className="inline-block px-3 py-1 rounded-full text-sm font-[Roboto] text-white"
              style={{ backgroundColor: "#FF00D2" }}
            >
              {t(`groups.${assessment.currentGroup.num}`)}
            </span>
            <div className="w-16 hidden md:block" />
          </div>
          <QuestionCard
            question={question.question}
            options={question.options}
            selectedAnswer={currentAnswer || null}
            onSelectAnswer={assessment.selectAnswer}
            onAutoNext={
              assessment.isLastQuestionInGroup
                ? assessment.currentGroupIndex === questionGroups.length - 1
                  ? assessment.finishAndGetResults
                  : assessment.next
                : assessment.next
            }
          />
          <div className="flex justify-center md:hidden">
            <img
              src={beOneLogo}
              alt="Be-one"
              width={46}
              height={20}
              className="h-5"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={assessment.previous}
            disabled={assessment.currentQuestion === 0}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-[Roboto] whitespace-nowrap w-36"
          >
            <ArrowLeft className="w-5 h-5" />
            {t("nav.previous")}
          </button>

          <div className="text-sm text-[#ffffff] text-center whitespace-nowrap">
            {assessment.answeredCount} / {assessment.totalQuestionsInTheme}
          </div>

          {assessment.isLastQuestionInGroup ? (
            <button
              type="button"
              onClick={
                assessment.currentGroupIndex === questionGroups.length - 1
                  ? assessment.finishAndGetResults
                  : assessment.next
              }
              disabled={!currentAnswer}
              className="flex items-center gap-2 rounded-lg bg-[#FF00D2] hover:bg-[#FF00D2]/90 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-[Roboto] px-6 py-3 whitespace-nowrap"
            >
              {t("nav.results")}
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={assessment.next}
              disabled={!currentAnswer}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-[Roboto] whitespace-nowrap w-36"
              style={{ backgroundColor: "#02324A" }}
              onMouseEnter={(e) => {
                if (currentAnswer)
                  e.currentTarget.style.backgroundColor = "#01263a";
              }}
              onMouseLeave={(e) => {
                if (currentAnswer)
                  e.currentTarget.style.backgroundColor = "#02324A";
              }}
            >
              {t("nav.next")}
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
